<script>
import { mapActions, mapGetters } from "vuex"
import Modal from "@/components/Modal.vue"
import CreateCompanyForm from "@/components/company/CreateCompanyForm.vue"
import ThemedLoading from "@/components/ThemedLoading.vue"

export default {
    name: "Dashboard",
    components: { ThemedLoading, CreateCompanyForm, Modal },
    data() {
        return {
            companies: [],
            showNewCompanyModal: false,
            isLoading: false,
            query: "",
        }
    },
    computed: {
        ...mapGetters("sidebar", {
            isSidebarLoading: "isSidebarLoading",
            selectedCompanyId: "companyId",
            sidebarMenu: "sidebarMenu",
        }),
        filteredCompanies: function () {
            const query = this.query.toLocaleLowerCase()

            if (query.length === 0) {
                return this.companies
            }

            return this.companies.filter((company) => {
                return company.name.toLocaleLowerCase().includes(query)
            })
        },
    },
    mounted() {
        document.body.classList.add("bg-reduxi")
        this.$store.commit("reduxi/setPageTitle", "")

        if (this.$permissions.hasAggregatorRole()) {
            this.fetchCompanies()
        }
    },
    created() {
        this.$store.dispatch("auth/checkIfUserSetUp", null, { root: true }).then((isSetUp) => {
            if (!isSetUp) {
                return this.$router.push("/setup")
            }
        })
    },
    methods: {
        ...mapActions("sidebar", {
            setCompany: "setCompany",
        }),
        fetchCompanies() {
            this.isLoading = true
            this.axios
                .get("/companies")
                .then((response) => {
                    this.companies = response.data.data.companies

                    if (this.companies.length === 1) {
                        this.onCompanyClick(this.companies[0])
                    }
                })
                .catch(() => {})
                .finally(() => {
                    this.isLoading = false
                })
        },
        onNewCompanyClick() {
            this.showNewCompanyModal = true
        },
        onCompanyClick(company) {
            this.setCompany(company)
            this.$router.push({
                path: `/companies/${company.id}/location-groups/${company.rootGroup.id}`,
            })
        },
        onCompanyCreated(company) {
            this.showNewCompanyModal = false
            this.fetchCompanies()
        },
    },
    destroyed() {
        document.body.classList.remove("bg-reduxi")
    },
}
</script>

<template>
    <div class="h-100">
        <ThemedLoading :active="isLoading" :is-full-page="true" />
        <!-- Page content-->
        <div v-if="$permissions.hasAggregatorRole()" class="container-fluid">
            <div class="row mb-4">
                <div class="col-12">
                    <div class="text-center">
                        <h2>{{ $t("Select company") }}</h2>
                        <div class="search-form input-group company-search">
                            <input
                                type="text"
                                name="query"
                                class="search form-control px-2"
                                v-model="query"
                                :placeholder="$t('Search')"
                                autocomplete="one-time-code"
                            />

                            <button
                                v-if="query.length > 0"
                                type="button"
                                class="btn bg-transparent"
                                style="margin-left: -40px; z-index: 100"
                                @click="query = ''"
                            >
                                <i class="fa fa-times text-primary"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row">
                <div
                    class="col-12 col-sm-6 col-md-4 col-lg-3 mb-4"
                    v-for="company in filteredCompanies"
                    :key="company.id"
                >
                    <div
                        class="card card-company"
                        @click.prevent="onCompanyClick(company)"
                        :class="{ active: company.id === selectedCompanyId }"
                    >
                        <div class="card-header">
                            <span class="fas fa-landmark fa-xl mr-4" /><span class="title">{{
                                company.name
                            }}</span>
                        </div>
                        <div class="card-body text-center">
                            <span class="fas fa-home mr-2" /><span class="number">{{
                                company.assignedControllersCount
                            }}</span>
                        </div>
                    </div>
                </div>
                <div
                    v-if="$permissions.hasSuperAdminRole()"
                    class="col-12 col-sm-6 col-md-4 col-lg-3 mb-4"
                    @click="onNewCompanyClick"
                >
                    <div class="card text-center card-company" role="button">
                        <div class="py-4">
                            <span class="text-primary fa fa-solid fa-xl fa-plus mr-2" />
                            <span class="text-primary">{{ $t("Add new") }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <Modal
            class="new-company-modal"
            v-if="showNewCompanyModal"
            @close="showNewCompanyModal = false"
        >
            <template #header>
                <h4>Create new company</h4>
                <button class="btn-close" @click="showNewCompanyModal = false">X</button>
            </template>
            <template #body>
                <CreateCompanyForm
                    @reset="showNewCompanyModal = false"
                    @created="onCompanyCreated"
                />
            </template>
            <template #footer></template>
        </Modal>
    </div>
</template>

<style scoped>
.card-company {
    box-shadow: 6px 6px 16px #00000070;
}
</style>
